import React from 'react';
import { Button } from '../components/ui/button';
import ContactButton from '../components/ContactButton';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-cream">
      {/* Header */}
      <header className="fixed top-0 w-full z-50 bg-charcoal/95 backdrop-blur-md border-b border-warmSage/20">
        <nav className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <a href="/" className="flex items-center space-x-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_fashion-empire-2/artifacts/bcsdxd12_IMG_2797.PNG" 
                alt="PRSVR Logo" 
                className="h-10"
                style={{ filter: 'brightness(0) saturate(100%) invert(98%) sepia(6%) saturate(214%) hue-rotate(327deg) brightness(102%) contrast(96%)' }}
              />
            </a>
            <Button onClick={() => window.location.href = '/'} variant="ghost" className="text-cream">
              Back to Home
            </Button>
          </div>
        </nav>
      </header>

      <div className="container mx-auto px-6 pt-32 pb-16 max-w-4xl">
        <h1 className="text-5xl font-black text-charcoal uppercase mb-8">Privacy Policy</h1>
        <p className="text-warmGray mb-8">Effective Date: {new Date().toLocaleDateString()}</p>

        <div className="space-y-8 text-charcoal">
          <section>
            <h2 className="text-2xl font-bold mb-4">1. Introduction</h2>
            <p className="text-warmGray leading-relaxed">
              PRSVR ("we," "our," or "us") respects your privacy and is committed to protecting your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or purchase our products and services.
            </p>
            <p className="text-warmGray leading-relaxed mt-4">
              By using our website, you consent to the practices described in this Privacy Policy. If you do not agree with this policy, please do not use our services.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">2. Information We Collect</h2>
            
            <h3 className="text-xl font-semibold mb-3 text-warmSage">2.1 Personal Information</h3>
            <p className="text-warmGray leading-relaxed mb-4">
              When you place an order or interact with our website, we may collect:
            </p>
            <ul className="list-disc list-inside text-warmGray space-y-2 ml-4 mb-4">
              <li><strong>Contact Information:</strong> Name, email address, phone number</li>
              <li><strong>Billing Information:</strong> Billing address, payment method details (processed securely through Shopify)</li>
              <li><strong>Shipping Information:</strong> Delivery address, recipient name</li>
              <li><strong>Custom Order Details:</strong> Team names, player names, player numbers, sizes, logo files</li>
              <li><strong>Account Information:</strong> Username, password (if you create an account)</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3 text-warmSage">2.2 Automatically Collected Information</h3>
            <p className="text-warmGray leading-relaxed mb-4">
              When you visit our website, we automatically collect certain information:
            </p>
            <ul className="list-disc list-inside text-warmGray space-y-2 ml-4">
              <li><strong>Device Information:</strong> IP address, browser type, operating system</li>
              <li><strong>Usage Data:</strong> Pages visited, time spent on pages, links clicked</li>
              <li><strong>Cookies and Tracking Technologies:</strong> Information collected through cookies, web beacons, and similar technologies</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">3. How We Use Your Information</h2>
            <p className="text-warmGray leading-relaxed mb-4">
              We use the information we collect for the following purposes:
            </p>
            <ul className="list-disc list-inside text-warmGray space-y-2 ml-4">
              <li><strong>Order Processing:</strong> To fulfill your orders, process payments, and arrange shipping</li>
              <li><strong>Custom Product Creation:</strong> To create personalized uniforms with your specified names, numbers, and logos</li>
              <li><strong>Communication:</strong> To send order confirmations, shipping updates, and respond to inquiries</li>
              <li><strong>Marketing:</strong> To send promotional emails about new products, special offers (you can opt-out anytime)</li>
              <li><strong>Service Improvement:</strong> To analyze usage patterns and improve our website and services</li>
              <li><strong>Fraud Prevention:</strong> To detect and prevent fraudulent transactions and protect our business</li>
              <li><strong>Legal Compliance:</strong> To comply with applicable laws and regulations</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">4. Information Sharing and Disclosure</h2>
            <p className="text-warmGray leading-relaxed mb-4">
              We do not sell your personal information. We may share your information with:
            </p>
            <ul className="list-disc list-inside text-warmGray space-y-2 ml-4">
              <li><strong>Service Providers:</strong> Third-party companies that help us operate our business (e.g., Shopify for payment processing, shipping carriers)</li>
              <li><strong>Business Partners:</strong> Trusted partners who assist in manufacturing and fulfilling custom orders</li>
              <li><strong>Legal Requirements:</strong> When required by law, court order, or to protect our rights and safety</li>
              <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">5. Data Security</h2>
            <p className="text-warmGray leading-relaxed mb-4">
              We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These measures include:
            </p>
            <ul className="list-disc list-inside text-warmGray space-y-2 ml-4">
              <li>Secure Socket Layer (SSL) encryption for data transmission</li>
              <li>Secure payment processing through Shopify's PCI-compliant platform</li>
              <li>Regular security assessments and updates</li>
              <li>Access controls and authentication procedures</li>
            </ul>
            <p className="text-warmGray leading-relaxed mt-4">
              However, no method of transmission over the internet is 100% secure. While we strive to protect your information, we cannot guarantee absolute security.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">6. Cookies and Tracking Technologies</h2>
            <p className="text-warmGray leading-relaxed mb-4">
              We use cookies and similar tracking technologies to enhance your browsing experience, analyze website traffic, and personalize content. You can control cookies through your browser settings, but disabling cookies may affect website functionality.
            </p>
            <p className="text-warmGray leading-relaxed">
              Types of cookies we use: Essential cookies (required for website operation), Performance cookies (analytics), and Marketing cookies (personalized advertising).
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">7. Your Privacy Rights</h2>
            <p className="text-warmGray leading-relaxed mb-4">
              Depending on your location, you may have the following rights:
            </p>
            <ul className="list-disc list-inside text-warmGray space-y-2 ml-4">
              <li><strong>Access:</strong> Request access to the personal information we hold about you</li>
              <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
              <li><strong>Deletion:</strong> Request deletion of your personal information (subject to legal obligations)</li>
              <li><strong>Opt-Out:</strong> Unsubscribe from marketing emails at any time</li>
              <li><strong>Data Portability:</strong> Request a copy of your data in a portable format</li>
              <li><strong>Object:</strong> Object to certain processing activities</li>
            </ul>
            <p className="text-warmGray leading-relaxed mt-4">
              To exercise these rights, contact us at info@prsvr.com. We will respond within 30 days.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">8. Data Retention</h2>
            <p className="text-warmGray leading-relaxed">
              We retain your personal information for as long as necessary to fulfill the purposes outlined in this Privacy Policy, comply with legal obligations, resolve disputes, and enforce our agreements. Custom order information is retained for at least 7 years for business and tax purposes.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">9. Third-Party Links</h2>
            <p className="text-warmGray leading-relaxed">
              Our website may contain links to third-party websites (e.g., social media platforms). We are not responsible for the privacy practices of these external sites. Please review their privacy policies before providing any personal information.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">10. Children's Privacy</h2>
            <p className="text-warmGray leading-relaxed">
              Our services are not directed to individuals under 18 years of age. We do not knowingly collect personal information from children. If you believe a child has provided us with personal information, please contact us immediately.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">11. California Privacy Rights</h2>
            <p className="text-warmGray leading-relaxed mb-4">
              California residents have additional rights under the California Consumer Privacy Act (CCPA):
            </p>
            <ul className="list-disc list-inside text-warmGray space-y-2 ml-4">
              <li>Right to know what personal information is collected, used, and shared</li>
              <li>Right to delete personal information (with certain exceptions)</li>
              <li>Right to opt-out of the sale of personal information (we do not sell personal information)</li>
              <li>Right to non-discrimination for exercising privacy rights</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">12. International Users</h2>
            <p className="text-warmGray leading-relaxed">
              If you are accessing our website from outside the United States, please be aware that your information may be transferred to, stored, and processed in the United States. By using our services, you consent to this transfer.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">13. Changes to This Privacy Policy</h2>
            <p className="text-warmGray leading-relaxed">
              We may update this Privacy Policy from time to time. Changes will be posted on this page with an updated "Effective Date." We encourage you to review this policy periodically. Continued use of our services after changes constitutes acceptance of the updated policy.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">14. Contact Us</h2>
            <p className="text-warmGray leading-relaxed mb-4">
              If you have questions, concerns, or requests regarding this Privacy Policy or our privacy practices, please contact us:
            </p>
            <div className="bg-charcoal text-cream p-6 rounded-lg">
              <p className="font-bold mb-2">PRSVR</p>
              <p>Email: info@prsvr.com</p>
              <p>Phone: (661) 553-2186</p>
              <p className="mt-4">We are committed to resolving any privacy concerns you may have.</p>
            </div>
          </section>
        </div>
      </div>

      <ContactButton />
    </div>
  );
};

export default PrivacyPolicy;